import { useEffect, useState } from "react";
import { MaskLine, Reveal, ClipReveal } from "@/components/Reveal";
import { Target, Eye, ShieldCheck, Award, FileBadge, ZoomIn } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import Lightbox from "@/components/Lightbox";
import JourneyTimeline from "@/components/JourneyTimeline";

const VALUES = [
  { icon: ShieldCheck, t: "Quality First", d: "Every panel, rack and cabin is built to industrial-grade standards and tested before it ships." },
  { icon: Target, t: "Accountability", d: "One partner, one point of contact — from the first drawing to final handover." },
  { icon: Eye, t: "Precision", d: "Engineering-led design ensures a perfect fit for your space, workflow and budget." },
];

const DEFAULTS = {
  // Editable section headings — all overridable from the Admin CMS.
  hero_eyebrow: "About Manik Engineering",
  hero_line1: "Engineering the",
  hero_line2: "spaces where India works.",
  md_eyebrow: "Managing Director",
  story_eyebrow: "Our Story",
  story_heading: "Building the workspaces where industry gets things done — since 1973.",
  values_heading: "Our Values",
  policy_eyebrow: "Our Policy",
  awards_eyebrow: "Awards & Accolades",
  awards_heading: "Recognised for the work.",
  patents_eyebrow: "Intellectual Property",
  patents_heading: "Ideas we own.",
  md_photo: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  md_name: "Mr. Kishin Manik",
  md_role: "Managing Director",
  md_quote:
    "We measure our success by the workspaces we've made safer, sharper and more productive — one factory, one warehouse, one office at a time.",
  story_p1:
    "Manik Engineering Pvt. Ltd. is a Mumbai-based manufacturer of modular office solutions, warehouse safety products, office furniture and industrial storage systems. From our corporate office in Bhandup and our manufacturing facility in Kopar Khairane, Navi Mumbai, we deliver turnkey workspace and warehouse solutions across India.",
  story_p2:
    "Under our flagship ModulArt brand, we design in-plant offices and demountable partition systems that transform raw industrial and commercial spaces into productive, professional environments — without the cost and disruption of permanent construction.",
  story_p3:
    "Because we own our manufacturing, we control quality, cost and timelines end-to-end. That means our clients get one accountable partner from concept and engineering through fabrication and on-site installation.",
  policy_image:
    "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  policy_title: "Our Quality Policy",
  policy_body:
    "Manik Engineering is committed to designing, manufacturing and installing modular workspace and industrial products that consistently exceed customer expectations for quality, safety and performance. We uphold ISO-aligned processes, invest in continuous improvement, and hold every partner in our supply chain to the same standard we hold ourselves. Precision. Accountability. Sustainability. Delivered on every project, at every scale.",
  awards: [
    { title: "MSME Excellence in Manufacturing", year: "2024", note: "Recognised for sustained innovation in modular workspace systems.", image: "" },
    { title: "Industry Choice — Warehouse Safety", year: "2023", note: "Awarded by a leading logistics infrastructure council.", image: "" },
    { title: "50 Years of Manufacturing", year: "2023", note: "Golden jubilee milestone since founding in 1973.", image: "" },
  ],
  patents: [
    { title: "Snap-fit Detachable Panel Partition System", year: "Filed 2022", note: "Novel quick-coupling upright and clip mechanism for tool-light assembly.", image: "" },
    { title: "Concealed MEP Cavity Wall Assembly", year: "Filed 2021", note: "Integrated services routing within 112 mm partition wall thickness.", image: "" },
  ],
};

export default function About() {
  const [c, setC] = useState(DEFAULTS);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const openLightbox = (src) => src && setLightboxSrc(src);
  const closeLightbox = () => setLightboxSrc(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/content");
        const raw = data?.data || {};
        // Drop empty entries from awards/patents lists so a saved [""] never leaks blanks to the page.
        const withoutBlankRows = { ...raw };
        for (const key of ["awards", "patents"]) {
          if (Array.isArray(raw[key])) {
            const kept = raw[key].filter((it) => it && (it.title || it.note || it.year || it.image));
            if (kept.length) withoutBlankRows[key] = kept;
            else delete withoutBlankRows[key];
          }
        }
        const clean = Object.fromEntries(
          Object.entries(withoutBlankRows).filter(([, v]) => {
            if (v === null || v === undefined) return false;
            if (typeof v === "string" && v.trim() === "") return false;
            if (Array.isArray(v) && v.length === 0) return false;
            return true;
          })
        );
        setC({ ...DEFAULTS, ...clean });
      } catch {
        /* fall back to defaults */
      }
    })();
  }, []);

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-[#eaf6fc] pt-40 pb-24 relative overflow-hidden border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative z-10">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-8">{c.hero_eyebrow}</p>
          <h1 className="font-display font-bold text-[#334155] text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.98]">
            <MaskLine delay={0.1}>{c.hero_line1}</MaskLine>
            <MaskLine delay={0.25}>
              {c.hero_line2?.includes(" ") ? (() => {
                const parts = c.hero_line2.split(" ");
                const last2 = parts.slice(-2).join(" ");
                const rest = parts.slice(0, -2).join(" ");
                return (<>{rest} <span className="text-[#3da8d8]">{last2}</span></>);
              })() : c.hero_line2}
            </MaskLine>
          </h1>
        </div>
      </section>

      {/* MD PHOTO + OUR STORY */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <ClipReveal className="lg:col-span-5">
            <div className="relative border border-black/10 bg-white">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={resolveImg(c.md_photo)}
                  alt={c.md_name}
                  data-testid="about-md-photo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 border-t border-black/10 bg-white">
                <p className="font-display text-xl text-[#334155]">{c.md_name}</p>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#3da8d8] mt-1">
                  {c.md_eyebrow || c.md_role}
                </p>
                <p className="text-sm text-[#52525B] leading-relaxed mt-4 italic border-l-2 border-[#78be27] pl-3">
                  &ldquo;{c.md_quote}&rdquo;
                </p>
              </div>
            </div>
          </ClipReveal>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-6">{c.story_eyebrow}</p>
              <h2 className="font-display text-3xl lg:text-5xl tracking-tight text-[#334155] leading-tight mb-8">
                {c.story_heading}
              </h2>
            </Reveal>
            <div className="space-y-6 text-lg text-[#52525B] leading-relaxed">
              <Reveal delay={0.05}><p>{c.story_p1}</p></Reveal>
              <Reveal delay={0.1}>
                <p>{c.story_p2}</p>
              </Reveal>
              <Reveal delay={0.15}><p>{c.story_p3}</p></Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* OUR JOURNEY (horizontal timeline) */}
      <JourneyTimeline />

      {/* OUR POLICY */}
      <section className="bg-white border-y border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ClipReveal className="border border-black/10">
              <div className="aspect-[1/1.414] overflow-hidden bg-[#F9FAFB]">
                <img
                  src={resolveImg(c.policy_image)}
                  alt="Our Quality Policy"
                  data-testid="about-policy-image"
                  className="w-full h-full object-cover"
                />
              </div>
            </ClipReveal>
            <div>
              <Reveal>
                <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-6">{c.policy_eyebrow}</p>
                <h2 className="font-display text-3xl lg:text-5xl tracking-tight text-[#334155] leading-tight mb-8">
                  {c.policy_title}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="text-lg text-[#52525B] leading-relaxed whitespace-pre-wrap"
                  data-testid="about-policy-body"
                >
                  {c.policy_body}
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-10 flex flex-wrap gap-3">
                  {["Precision", "Accountability", "Sustainability"].map((k) => (
                    <span key={k} className="font-mono-accent text-[11px] uppercase tracking-widest border border-[#78be27]/40 text-[#78be27] px-3 py-1.5">
                      {k}
                    </span>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
          <Reveal><h2 className="font-display text-4xl lg:text-6xl tracking-tight mb-16">{c.values_heading}</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.1} className="bg-white">
                <div className="p-10 h-full hover:bg-[#F9FAFB] transition-colors duration-500">
                  <v.icon size={34} className="text-[#78be27] mb-8" strokeWidth={1.5} />
                  <h3 className="font-display text-2xl mb-4">{v.t}</h3>
                  <p className="text-[#52525B] leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AWARDS + PATENTS */}
      <section className="bg-[#F9FAFB]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-14 lg:gap-20">
          <div data-testid="about-awards">
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <Award size={28} className="text-[#78be27]" strokeWidth={1.5} />
                <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8]">{c.awards_eyebrow}</p>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-[#334155] mb-10">{c.awards_heading}</h2>
            </Reveal>
            <div className="space-y-5">
              {(c.awards || []).map((a, i) => (
                <AwardPatentRow key={i} item={a} accent="#78be27" idx={i} testid={`award-${i}`} onOpen={openLightbox} />
              ))}
            </div>
          </div>

          <div data-testid="about-patents">
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <FileBadge size={28} className="text-[#3da8d8]" strokeWidth={1.5} />
                <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8]">{c.patents_eyebrow}</p>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-[#334155] mb-10">{c.patents_heading}</h2>
            </Reveal>
            <div className="space-y-5">
              {(c.patents || []).map((p, i) => (
                <AwardPatentRow key={i} item={p} accent="#3da8d8" idx={i} testid={`patent-${i}`} onOpen={openLightbox} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <Lightbox src={lightboxSrc} open={!!lightboxSrc} onClose={closeLightbox} alt="Certificate" />
    </div>
  );
}

/**
 * Row for an Award or Patent entry.
 * Left: white text card with title/note/year (unchanged design).
 * Right: A4-portrait image thumb by default. When the loaded image is
 * landscape, the thumb switches to A4-landscape (1.414:1).
 */
function AwardPatentRow({ item, accent, testid, onOpen }) {
  const [isLandscape, setIsLandscape] = useState(false);
  const imgSrc = item.image ? resolveImg(item.image) : "";

  return (
    <Reveal>
      <div
        data-testid={`about-${testid}`}
        className={`grid gap-4 border border-black/10 bg-white hover:border-[${accent}]/60 transition-colors ${
          imgSrc ? "grid-cols-1 sm:grid-cols-[1fr_auto]" : "grid-cols-1"
        }`}
      >
        <div className="p-6 flex items-start justify-between gap-6">
          <div>
            <p className="font-display text-lg text-[#334155] leading-snug">{item.title}</p>
            {item.note && <p className="text-sm text-[#52525B] leading-relaxed mt-2 whitespace-pre-wrap">{item.note}</p>}
          </div>
          {item.year && (
            <span className="font-mono-accent text-[10px] uppercase tracking-widest shrink-0" style={{ color: accent }}>
              {item.year}
            </span>
          )}
        </div>
        {imgSrc && (
          <button
            type="button"
            onClick={() => onOpen(imgSrc)}
            data-testid={`about-${testid}-image-btn`}
            className={`relative group overflow-hidden border-l border-black/10 bg-[#F9FAFB] self-center mr-4 sm:mr-6 my-4 ${
              isLandscape
                ? "w-full sm:w-[220px] aspect-[1.414/1]"
                : "w-full sm:w-[110px] aspect-[1/1.414]"
            }`}
            aria-label="Open certificate"
          >
            <img
              src={imgSrc}
              alt=""
              className="w-full h-full object-cover"
              onLoad={(e) => {
                const { naturalWidth, naturalHeight } = e.currentTarget;
                setIsLandscape(naturalWidth > naturalHeight);
              }}
            />
            <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
              <ZoomIn size={22} />
            </span>
          </button>
        )}
      </div>
    </Reveal>
  );
}
